import { forwardRef, useImperativeHandle, useRef } from "react";
import { Eraser } from "lucide-react";

/** Cadre de signature au doigt ou à la souris. `ref.current.toDataURL()` renvoie "" si rien n'est tracé. */
export const SignaturePad = forwardRef(({ testId = "signature-pad", height = 160, className = "" }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const touched = useRef(false);

  useImperativeHandle(ref, () => ({
    toDataURL: () => (touched.current ? canvasRef.current?.toDataURL("image/png") || "" : ""),
    clear: () => {
      const c = canvasRef.current;
      c?.getContext("2d").clearRect(0, 0, c.width, c.height);
      touched.current = false;
    },
  }));

  const point = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    const scale = canvasRef.current.width / rect.width;
    return { x: (p.clientX - rect.left) * scale, y: (p.clientY - rect.top) * scale };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    touched.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = point(e);
    ctx.strokeStyle = "#0F2645";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stop = () => { drawing.current = false; };

  return (
    <div className={className}>
      <canvas
        ref={canvasRef}
        data-testid={`${testId}-canvas`}
        width={560}
        height={height}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={stop}
        onMouseLeave={stop}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={stop}
        className="w-full touch-none rounded-xl border border-dashed border-[#E2E7F7] bg-[#F6F8FD]"
      />
      <button
        type="button"
        data-testid={`${testId}-clear`}
        onClick={() => ref.current?.clear()}
        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#5C6A87] hover:text-slate-800 transition-colors duration-200"
      >
        <Eraser className="w-3 h-3" /> Effacer
      </button>
    </div>
  );
});
